import { ViewIds } from '@ardoq/api-types';
import { type FeatureContext, featureOperations } from '@ardoq/features';
import { AppModules } from 'appContainer/types';

/**
 * Why the app is showing the workspace access pattern, and therefore how much
 * of its chrome belongs on screen. `singleView` is the shape an org without
 * workspace mode needs: the view that was loaded, on its own and read-only,
 * whether it came from a slide or anywhere else.
 *
 * An open scenario has two shapes, split by the one view it offers. Block
 * Diagram is the scenario's working surface, so `scenario` covers both working
 * in the scenario and showing a Block Diagram slide from it — there is nothing
 * to tell apart. `scenarioSlide` is a slide that saved some other view: it
 * renders what it saved, read-only, because that visualization is not one this
 * chrome can offer or author.
 */
export type FocusedChrome =
  | { kind: 'none' }
  | { kind: 'scenario' }
  | { kind: 'scenarioSlide' }
  | { kind: 'singleView' };

const NO_FOCUSED_CHROME: FocusedChrome = { kind: 'none' };

// The one view scenario chrome offers, and the one it coerces every other view
// to, because it is the only view in VIEW_SUPPORTING_DIFF.
const SCENARIO_CHROME_VIEW_ID = ViewIds.BLOCK_DIAGRAM;

type ShouldClosePresentationOnOpenScenarioArgs = {
  featureContext: FeatureContext;
  isShowingSlide: boolean;
  isPresentationSlide: boolean;
};

/**
 * A presentation whose slide is still on screen is stale once the user opens a
 * scenario of their own, and scenario chrome has no breadcrumb back to the deck
 * to leave it reachable from. A slide opens its own scenario and keeps its
 * presentation. A presentation the user attached while working in a scenario
 * shows no slide and stays, so a deck can span several scenarios.
 */
const shouldClosePresentationOnOpenScenario = ({
  featureContext,
  isShowingSlide,
  isPresentationSlide,
}: ShouldClosePresentationOnOpenScenarioArgs): boolean =>
  isShowingSlide &&
  !isPresentationSlide &&
  featureOperations.isHardNaxEnabled(featureContext);

type ShouldConfirmCloseScenarioArgs = {
  featureContext: FeatureContext;
  isShowingSlide: boolean;
};

/**
 * The confirm exists to tell a user who opened a scenario that leaving loads
 * their mainline. A slide's scenario was never their choice, so there is
 * nothing to warn them about, and a slide on screen tells the two apart.
 */
const shouldConfirmCloseScenario = ({
  featureContext,
  isShowingSlide,
}: ShouldConfirmCloseScenarioArgs): boolean =>
  !(isShowingSlide && featureOperations.isHardNaxEnabled(featureContext));

type ScenarioOpenViewIdArgs = {
  featureContext: FeatureContext;
  isPresentationSlide: boolean;
  isReopeningActiveScenario: boolean;
};

/**
 * The view a scenario opens on, or null to leave the choice alone. Scenario
 * chrome offers one pair and enters on its diff-capable half, whatever the
 * session was looking at before. Only an entry decides the view: reopening the
 * scenario already on screen is a reload, which must keep the half the user
 * chose. A slide brings its own view and keeps it, and without scenario chrome
 * the workspace's start view still decides.
 */
const getScenarioOpenViewId = ({
  featureContext,
  isPresentationSlide,
  isReopeningActiveScenario,
}: ScenarioOpenViewIdArgs): ViewIds | null =>
  !isPresentationSlide &&
  !isReopeningActiveScenario &&
  featureOperations.isHardNaxEnabled(featureContext)
    ? SCENARIO_CHROME_VIEW_ID
    : null;

/**
 * The view focused chrome opens on when nothing else names one. Only scenario
 * chrome names one: every other shape shows the view that was loaded, whether a
 * slide named it or not.
 */
const getStartViewId = (focusedChrome: FocusedChrome): ViewIds | null =>
  focusedChrome.kind === 'scenario' ? SCENARIO_CHROME_VIEW_ID : null;

/**
 * The slide on screen, if any. A visualization slide carries the view it saved;
 * any other slide (dashboard, report, Lucid, metamodel) renders its own content
 * in its own module, so focused chrome has nothing to say about it.
 */
export type SlideOnScreen =
  | { kind: 'none' }
  | { kind: 'visualization'; viewId: ViewIds }
  | { kind: 'other' };

type ToFocusedChromeArgs = {
  isInScenarioMode: boolean;
  featureContext: FeatureContext;
  isViewpointMode: boolean;
  slideOnScreen: SlideOnScreen;
};

const toFocusedChrome = ({
  isInScenarioMode,
  featureContext,
  isViewpointMode,
  slideOnScreen,
}: ToFocusedChromeArgs): FocusedChrome => {
  /**
   * Viewpoint mode is its own access pattern with its own chrome, and it shares
   * MainAppModule with workspace mode. Collapsing it would take its top bar and
   * its view switching away.
   */
  if (isViewpointMode) {
    return NO_FOCUSED_CHROME;
  }
  /**
   * Focused chrome is for showing a visualization as its slide saved it. Any
   * other slide renders its own content and stays editable.
   */
  if (slideOnScreen.kind === 'other') {
    return NO_FOCUSED_CHROME;
  }
  if (!featureOperations.isHardNaxEnabled(featureContext)) {
    return NO_FOCUSED_CHROME;
  }
  if (!isInScenarioMode) {
    return { kind: 'singleView' };
  }
  /**
   * A slide names its own view, which scenario chrome would otherwise coerce to
   * the one view it offers — the bug ardoq-packages#35968 fixed. A slide that
   * saved that same view needs no separate shape, so clicking one in the
   * sidebar of a deck the user is building leaves the scenario as it was.
   */
  return slideOnScreen.kind === 'none' ||
    slideOnScreen.viewId === SCENARIO_CHROME_VIEW_ID
    ? { kind: 'scenario' }
    : { kind: 'scenarioSlide' };
};

const isFocusedChrome = (focusedChrome: FocusedChrome): boolean =>
  focusedChrome.kind !== 'none';

const isSingleViewChrome = (focusedChrome: FocusedChrome): boolean =>
  focusedChrome.kind === 'singleView';

/**
 * Either shape an open scenario takes. Both keep the scenario navbar, which is
 * what keeps the left navigator on screen: `MainAppModuleView` renders
 * `MainAppLayout` without `MainAppModuleSidebar` on its focused-navbar branch.
 */
const isScenarioChrome = (focusedChrome: FocusedChrome): boolean =>
  focusedChrome.kind === 'scenario' || focusedChrome.kind === 'scenarioSlide';

/**
 * A chrome showing a slide shows it as the slide shows it, so nothing on screen
 * may change the data behind it. Scenario chrome is the exception: Block Diagram
 * is the surface a scenario exists to be worked on, whether a slide named it or
 * the user opened the scenario directly.
 */
const isReadOnlyVisualization = (focusedChrome: FocusedChrome): boolean =>
  focusedChrome.kind === 'singleView' || focusedChrome.kind === 'scenarioSlide';

/**
 * Whether this chrome offers "Add to presentation". It tracks
 * {@link isReadOnlyVisualization} exactly: a read-only rendering of a
 * visualization this chrome cannot offer is not one to author a slide from,
 * and in a scenario that leaves Block Diagram as the only slide source.
 */
const allowsAddToPresentation = (focusedChrome: FocusedChrome): boolean =>
  !isReadOnlyVisualization(focusedChrome);

type IsSlideReadOnlyArgs = {
  featureContext: FeatureContext;
  isViewpointSlide: boolean;
  isScenarioSlide: boolean;
  /** The slide as it is on screen once opened. */
  slideOnScreen: SlideOnScreen;
};

/**
 * Whether opening this slide would give a read-only visualization, which
 * {@link toFocusedChrome} answers only for the slide already open. A slide list
 * needs it per slide, and asks the same question of the chrome each slide would
 * open into so the badge cannot disagree with what opening it does.
 */
const isSlideReadOnly = ({
  featureContext,
  isViewpointSlide,
  isScenarioSlide,
  slideOnScreen,
}: IsSlideReadOnlyArgs): boolean =>
  isReadOnlyVisualization(
    toFocusedChrome({
      featureContext,
      isViewpointMode: isViewpointSlide,
      isInScenarioMode: isScenarioSlide,
      slideOnScreen,
    })
  );

type ToModuleChromeArgs = {
  focusedChrome: FocusedChrome;
  selectedModule: AppModules;
};

/**
 * The chrome the module on screen renders. `focusedChrome$` describes the
 * workspace access pattern but is global, so an org with single-view chrome
 * reads `singleView` on every page. Only the workspace module renders it; every
 * other module (dashboard, report, metamodel, Lucid) keeps its own menus and
 * stays a slide source. Anything shown outside the workspace module asks this,
 * not `focusedChrome$`.
 */
const toModuleChrome = ({
  focusedChrome,
  selectedModule,
}: ToModuleChromeArgs): FocusedChrome =>
  selectedModule === AppModules.WORKSPACES ? focusedChrome : NO_FOCUSED_CHROME;

/**
 * A read-only visualization has nothing to change it with, so a right-click on
 * it has nothing to offer. Scenario chrome is for working on a scenario and
 * keeps its menus.
 */
const isContextMenuDisabled = (args: ToModuleChromeArgs): boolean =>
  isReadOnlyVisualization(toModuleChrome(args));

/**
 * Focused chrome has no context bar, so moving the context component or
 * workspace would change what the view shows with nothing on screen saying so
 * and no way back to where the user was.
 */
const allowsContextNavigation = (focusedChrome: FocusedChrome): boolean =>
  !isFocusedChrome(focusedChrome);

/**
 * `canDisplayTab` admits the active tab ids whatever this returns, so
 * `singleView` collapses to the view that was loaded without anything having to
 * name it. `fallbackViewId` is what a session with no active view lands on —
 * without it the tab list would be empty and no view would render at all.
 *
 * Scenario chrome names one view rather than a fallback, so a scenario opened
 * on anything else resolves to it.
 */
const getAllowedViewIds = (
  focusedChrome: FocusedChrome,
  fallbackViewId: ViewIds
): ViewIds[] => {
  switch (focusedChrome.kind) {
    case 'scenario':
      return [SCENARIO_CHROME_VIEW_ID];
    case 'scenarioSlide':
    case 'singleView':
      return [fallbackViewId];
    case 'none':
      return [];
  }
};

const replaceViewIfNotAllowed = (
  focusedChrome: FocusedChrome,
  viewId: ViewIds
): ViewIds => {
  if (focusedChrome.kind !== 'scenario') {
    return viewId;
  }
  return viewId === ViewIds.NONE ? viewId : SCENARIO_CHROME_VIEW_ID;
};

export const focusedChromeOperations = {
  NO_FOCUSED_CHROME,
  toFocusedChrome,
  shouldClosePresentationOnOpenScenario,
  shouldConfirmCloseScenario,
  getScenarioOpenViewId,
  getStartViewId,
  isFocusedChrome,
  isSingleViewChrome,
  isScenarioChrome,
  allowsAddToPresentation,
  isSlideReadOnly,
  allowsContextNavigation,
  isReadOnlyVisualization,
  toModuleChrome,
  isContextMenuDisabled,
  getAllowedViewIds,
  replaceViewIfNotAllowed,
};
